import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@/store/features/baseRtkQuery';
import type {
  DashboardConfig,
  BatchToolCallRequest,
  BatchToolCallResponse,
  ExecuteQueryRequest,
  ExecuteQueryResponse,
  ExecuteActionRequest,
  ExecuteActionResponse,
  SaveLayoutRequest,
  FilterOption,
  LayoutItem,
} from '@/lib/dashboard/types';

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['Dashboard', 'DataSource', 'FilterOptions'],
  endpoints: (builder) => ({
    // Get dashboard configuration
    getDashboard: builder.query<{
      config: DashboardConfig;
      inlineData?: Record<string, unknown> | null;
      metadata?: import('@/lib/dashboard/types').DashboardMetadata | null;
    }, string>({
      query: (dashboardId) => `/api/dashboards/${dashboardId}`,
      providesTags: (result, error, id) => [{ type: 'Dashboard', id }],
    }),

    // Batch fetch tool call data (ad-hoc mode)
    getToolCallData: builder.query<BatchToolCallResponse, BatchToolCallRequest>({
      query: (body) => ({
        url: '/api/dashboards/tool-calls/batch',
        method: 'POST',
        body,
      }),
      providesTags: ['DataSource'],
    }),

    // Refresh tool calls by re-executing them
    refreshToolCalls: builder.mutation<BatchToolCallResponse, BatchToolCallRequest>({
      query: (body) => ({
        url: '/api/dashboards/tool-calls/refresh',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DataSource'],
    }),

    // Execute parameterized query (template mode)
    executeQuery: builder.mutation<
      ExecuteQueryResponse,
      { dashboardId: string; sourceId: string; filters: Record<string, unknown> }
    >({
      query: ({ dashboardId, sourceId, filters }) => ({
        url: `/api/dashboards/${dashboardId}/sources/${sourceId}/query`,
        method: 'POST',
        body: { filters },
      }),
    }),

    // Get filter options (dynamic)
    getFilterOptions: builder.query<
      { options: FilterOption[] },
      { dashboardId: string; filterId: string; parentFilters?: Record<string, string> }
    >({
      query: ({ dashboardId, filterId, parentFilters }) => ({
        url: `/api/dashboards/${dashboardId}/filters/${filterId}/options`,
        params: parentFilters,
      }),
      providesTags: (result, error, { filterId }) => [
        { type: 'FilterOptions', id: filterId },
      ],
    }),

    // Save user layout
    saveLayout: builder.mutation<
      { success: boolean },
      { dashboardId: string; layout: LayoutItem[] }
    >({
      query: ({ dashboardId, layout }) => ({
        url: `/api/dashboards/${dashboardId}/layout`,
        method: 'POST',
        body: { layout },
      }),
      invalidatesTags: (result, error, { dashboardId }) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    // Execute row action
    executeAction: builder.mutation<
      ExecuteActionResponse,
      {
        dashboardId: string;
        actionId: string;
        rowData: Record<string, unknown>;
        formData?: Record<string, unknown>;
      }
    >({
      query: ({ dashboardId, actionId, rowData, formData }) => ({
        url: `/api/dashboards/${dashboardId}/actions/${actionId}`,
        method: 'POST',
        body: { rowData, formData },
      }),
    }),

    // Generic API call for custom action endpoints
    callActionEndpoint: builder.mutation<
      unknown,
      { url: string; method: string; body?: Record<string, unknown> }
    >({
      query: ({ url, method, body }) => ({
        url,
        method,
        body,
      }),
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetToolCallDataQuery,
  useLazyGetToolCallDataQuery,
  useRefreshToolCallsMutation,
  useExecuteQueryMutation,
  useGetFilterOptionsQuery,
  useSaveLayoutMutation,
  useExecuteActionMutation,
  useCallActionEndpointMutation,
} = dashboardApi;
